import { Link } from "react-router"

import type { Cargo, Departamento } from "@/componentes/cargos/cargo"
import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"

/** Un área con sus puestos y, si se puede administrar, sus acciones. */
export function TarjetaDepartamento({
  departamento: d,
  puestos,
  puedeAdministrar,
  puedeVerCargos,
  ocupado,
  onEditar,
  onArchivar,
  onReactivar,
}: {
  departamento: Departamento
  /** `undefined` mientras el catálogo de cargos no llega. */
  puestos: Cargo[] | undefined
  puedeAdministrar: boolean
  puedeVerCargos: boolean
  /** Hay un cambio de estado en curso: no se ofrece otro. */
  ocupado: boolean
  onEditar: () => void
  onArchivar: () => void
  onReactivar: () => void
}) {
  return (
    <li data-testid={`departamento-${d.id}`} className="tarjeta flex flex-col gap-3">
      <div className="flex flex-wrap items-start gap-2">
        <h2
          className="min-w-0 flex-1 text-base font-semibold"
          style={d.activo ? undefined : { color: "var(--fg-muted)" }}
        >
          {d.codigo && (
            <span className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
              {d.codigo} ·{" "}
            </span>
          )}
          {d.nombre}
        </h2>
        {d.siglas && <Insignia tono="neutro">{d.siglas}</Insignia>}
        {!d.activo && <Insignia tono="neutro">Archivado</Insignia>}
      </div>
      <ResponsableDelArea departamento={d} />
      {d.descripcion && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {d.descripcion}
        </p>
      )}
      <PuestosDelArea puestos={puestos} cuantos={d.cargos} enlazar={puedeVerCargos} />
      {puedeAdministrar && (
        <div
          className="mt-auto flex flex-wrap gap-2 border-t pt-3"
          style={{ borderColor: "var(--cristal-filo)" }}
        >
          {/* El nombre del área va en el nombre accesible: con varias
              tarjetas, «Editar» a secas no dice cuál. Empieza por lo que se
              lee en el botón (WCAG 2.5.3), para que quien lo dicta lo encuentre. */}
          <Boton tamano="sm" aria-label={`Editar «${d.nombre}»`} onClick={onEditar}>
            Editar
          </Boton>
          {d.activo ? (
            <Boton tamano="sm" aria-label={`Archivar «${d.nombre}»`} onClick={onArchivar}>
              Archivar
            </Boton>
          ) : (
            <Boton
              tamano="sm"
              aria-label={`Volver a activar «${d.nombre}»`}
              disabled={ocupado}
              onClick={onReactivar}
            >
              Volver a activar
            </Boton>
          )}
        </div>
      )}
    </li>
  )
}

/**
 * Quién responde del área y de cuál depende.
 *
 * ⚠ **Se dice el CARGO y, si lo ocupa una sola persona, su nombre.** Sin ocupante
 * único se dice el cargo a secas: inventar un nombre entre dos sería peor que no
 * ponerlo.
 */
function ResponsableDelArea({ departamento: d }: { departamento: Departamento }) {
  if (d.cargo_responsable === null && d.padre === null) return null
  return (
    <dl className="grid gap-1 text-sm">
      {d.cargo_responsable !== null && (
        <div className="flex flex-wrap gap-x-2">
          <dt style={{ color: "var(--fg-muted)" }}>Responsable</dt>
          <dd>
            {d.cargo_responsable}
            {d.responsable_ocupante !== null && ` — ${d.responsable_ocupante}`}
            {d.responsable_interino && (
              <span className="ml-2">
                <Insignia tono="aviso">interino</Insignia>
              </span>
            )}
          </dd>
        </div>
      )}
      {d.padre !== null && (
        <div className="flex flex-wrap gap-x-2">
          <dt style={{ color: "var(--fg-muted)" }}>Depende de</dt>
          <dd>{d.padre}</dd>
        </div>
      )}
    </dl>
  )
}

/**
 * Los puestos de un área, cada uno enlazado a su ficha en «Cargos».
 *
 * `puestos` es `undefined` mientras el catálogo no llega: entonces se dice cuántos
 * —lo cuenta el servidor— y no «ninguno», que sería mentira durante un segundo.
 */
export function PuestosDelArea({
  puestos,
  cuantos,
  enlazar,
}: {
  puestos: Cargo[] | undefined
  cuantos: number
  enlazar: boolean
}) {
  const total = puestos?.length ?? cuantos
  return (
    <div>
      <p className="rotulo mb-2">
        {total} cargo{total === 1 ? "" : "s"}
      </p>
      {puestos !== undefined && puestos.length === 0 && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Ninguno todavía.
        </p>
      )}
      {puestos !== undefined && puestos.length > 0 && (
        <ul className="flex flex-wrap gap-1.5">
          {puestos.map((c) => {
            const texto = `${c.nombre}${c.activo ? "" : " (retirado)"}`
            const clase =
              "inline-flex min-h-7 items-center rounded-[var(--radius-control)] border px-2.5 py-1 text-xs"
            const estilo = { borderColor: "var(--cristal-filo)", opacity: c.activo ? 1 : 0.6 }
            return (
              <li key={c.id}>
                {enlazar ? (
                  <Link
                    to={`/planificacion/cargos?cargo=${c.id}`}
                    className={`foco-cristal toque-cristal ${clase}`}
                    style={estilo}
                  >
                    {texto}
                  </Link>
                ) : (
                  <span className={clase} style={estilo}>
                    {texto}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
